import React, { FC } from "react";
import classNames from "classnames/dedupe";
import { mergeDeepRight, mergeDeepWith, concat } from "ramda";
import { IPromoWidgetProps } from "./PromoWidget.interface";
import { Typography } from "../../Fundamentals/Typography";
import PromoWidgetActions from "./PromoWidgetActions";
import WidgetMedia from "../WidgetMedia/WidgetMedia";
import mergeDeepWithDefaults from "../../../helpers/mergeDeepWithDefaults";
import deepClone from "../../../helpers/deepClone";
import MediaBuilder, {
  IMediaBuilderProps,
} from "../../Utility/MediaBuilder/MediaBuilder";
import { IImageData } from "../../Utility/MediaBuilder/Image";
import { CustomImageWrapper } from "../../Utility/CustomImageWrapper";

import { getVariants } from "./utils";

const rawVariants = getVariants();

const variants = deepClone(rawVariants);

/** Extends with defaults.
 *  For example, 'primary' variant will have all values of 'default' + own overrides from the raw config
 */
Object.keys(rawVariants).forEach((sizeKey) => {
  variants[sizeKey] = {
    ...mergeDeepWithDefaults(
      rawVariants[sizeKey],
      deepClone(rawVariants[sizeKey].default),
    ),
  };
});

export { variants as PromoWidgetDefaultVariantsSettings };

const PromoWidget: FC<IPromoWidgetProps> = ({
  id,
  size = "large",
  type = "default",
  link,
  ratio,
  iconMediaData,
  heading,
  title,
  description,
  actions,
  mediaData,
  customMedia,
  classes,
  variantsConfig,
  layoutSpace = "default",
  children,
}) => {
  const styleSetting = (variantsConfig ?? variants)[size][type];
  const wrapperClasses = classNames(
    classes?.wrapper,
    styleSetting?.text,
    styleSetting?.wrapper,
    styleSetting?.background,
  );
  return (
    <div
      className={classNames(classes?.root, styleSetting?.root, {
        "widget--sidebar-layout": layoutSpace === "withSidebar",
      })}
      data-testid="PromoWidget"
    >
      <div className={wrapperClasses}>
        {iconMediaData ? (
          <MediaBuilder
            {...(mergeDeepRight(
              { data: styleSetting?.iconProps ?? {} },
              iconMediaData,
            ) as IMediaBuilderProps)}
          />
        ) : null}
        {heading ? (
          <Typography
            {...mergeDeepWith(concat, styleSetting?.headingProps ?? {}, {
              classes: {
                root: classes?.heading ? ` ${classes.heading}` : "",
              },
            })}
            dangerouslySetInnerHTML={{ __html: heading }}
          />
        ) : null}
        <Typography
          {...mergeDeepWith(concat, styleSetting?.titleProps ?? {}, {
            classes: { root: classes?.title ? ` ${classes?.title}` : `` },
          })}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {description ? (
          <Typography
            {...mergeDeepWith(concat, styleSetting?.descriptionProps ?? {}, {
              classes: {
                root: classes?.description ? ` ${classes?.description}` : ``,
              },
            })}
            dangerouslySetInnerHTML={{ __html: description }}
          />
        ) : null}
        {actions?.length ? (
          <PromoWidgetActions
            id={id}
            link={link}
            actions={actions}
            type={type}
            classes={styleSetting?.actions}
          />
        ) : null}
      </div>
      {mediaData ? (
        <WidgetMedia
          classes={{ root: classNames(`widget--media`, classes?.media?.root) }}
          mediaData={mediaData}
          ratio={
            /** root component's ratio or image's data ratio or preset's ratio */
            ratio ??
            (mediaData.data as IImageData)?.ratio ??
            styleSetting?.ratio
          }
        />
      ) : null}
      {customMedia && (
        <div className={`widget--media ${classes?.media?.root ?? ""}`}>
          <div className="ratio ratio-img ratio--full-height">
            <CustomImageWrapper ratio={ratio ?? "16x9"}>
              {customMedia}
            </CustomImageWrapper>
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default PromoWidget;
